import Card from '@material-ui/core/Card';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import MuiFormLabel from '@material-ui/core/FormLabel';
import InputLabel from '@material-ui/core/InputLabel';
import Radio, { RadioProps } from '@material-ui/core/Radio';
import MuiRadioGroup from '@material-ui/core/RadioGroup';
import React, { useCallback } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import { AI_AGENTS_PREFIX } from '../../constants/aiAgents';
import { focusOutline } from '../../styles/focus';
import { isRequired } from '../../utils/validators';
import { Main } from '../Main';
import { VisuallyHidden } from '../VisuallyHidden';
import { getFieldNameWithPrefix } from './fieldUtils';

const FormLabel = styled(MuiFormLabel)`
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.gray333};

  &.Mui-focused {
    color: ${({ theme }) => theme.colors.gray333};
  }
` as typeof MuiFormLabel;

const RadioButton = styled(Card)`
  box-sizing: content-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 12rem;
  height: 1.5rem;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.125rem;
  color: ${({ theme }) => theme.colors.gray333};
  text-align: left;
  border: solid 1px ${({ theme }) => theme.colors.grayea};
  box-shadow: none;

  --background-color: ${({ theme }) => theme.colors.white};

  & svg {
    flex-shrink: 0;
    width: 1.2rem;
    min-width: 1.2rem;
    height: 1.2rem;
    color: ${({ theme }) => theme.colors.gray666};
    fill: ${({ theme }) => theme.colors.gray666};
  }

  &.selected {
    color: ${({ theme }) => theme.colors.white};
    background-color: ${({ theme }) => theme.colors.builderBlue};
    border-color: ${({ theme }) => theme.colors.graybbb};

    --background-color: ${({ theme }) => theme.colors.builderBlue};

    & svg {
      color: ${({ theme }) => theme.colors.white};
      fill: ${({ theme }) => theme.colors.white};
    }
  }
`;

const RadioGroup = styled(MuiRadioGroup)<{ $rowCount: number }>`
  display: grid;
  grid-template-rows: ${({ $rowCount }) => `repeat(${$rowCount}, auto)`};
  grid-auto-flow: column;
  gap: 0.75rem;
  margin: 0.5rem 0;

  & .MuiIconButton-root {
    padding: 0;
    margin: 0;
    border-radius: 0.25rem;

    &:hover {
      background-color: inherit;
      box-shadow: 0 4px 8px ${({ theme }) => theme.colors.black30};
    }

    &.Mui-checked {
      &:hover {
        background-color: ${({ theme }) => theme.colors.builderBlue};
      }
    }

    &.Mui-focusVisible {
      ${focusOutline()}
    }
  }
`;

const RadioLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const RadioSubLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 400;
  opacity: 0.75;
`;

type TypeRadioProps = RadioProps & {
  label: React.ReactNode;
};

function TypeRadio({ label, ...props }: TypeRadioProps) {
  return (
    <Radio
      disableRipple
      color="default"
      checkedIcon={<RadioButton className="selected">{label}</RadioButton>}
      icon={<RadioButton>{label}</RadioButton>}
      {...props}
    />
  );
}

function radioInputPropsWithTestId(
  testId: string,
): TypeRadioProps['inputProps'] {
  return { 'data-testid': testId } as TypeRadioProps['inputProps'];
}

export type FieldTypeDisplayInfo = {
  icon: React.ReactElement;
  label: string;
  subLabel?: string;

  /**
   * to allow multiple field types to be "grouped" and fall under one field type picker,
   * and get distinguished into their subtypes elsewhere (e.g. single location and multi location
   * fields both being in the "Location" group)
   */
  groupLabel?: string;
};

interface FieldTypeSelectorProps<FieldType> {
  availableFieldTypes: readonly FieldType[];
  getFieldTypeDisplay: (type: FieldType) => FieldTypeDisplayInfo;
  instructions?: string;
  prefix?: string;
  /** Number of rows before wrapping to a new column. */
  rowCount: number;
}

export function FieldTypeSelector<FieldType extends string>({
  availableFieldTypes,
  getFieldTypeDisplay,
  instructions,
  prefix,
  rowCount,
}: FieldTypeSelectorProps<FieldType>) {
  const { input, meta } = useField<FieldType>(
    getFieldNameWithPrefix('type', prefix),
    {
      validate: isRequired,
    },
  );
  const hasError = !!meta.error && (meta.touched || meta.modified);
  const onChange = useCallback((type) => () => input.onChange(type), [input]);
  return (
    <Main>
      <FormControl component="fieldset" error={hasError}>
        <FormLabel component="legend">
          {instructions || 'Select a field type.'}
        </FormLabel>
        <RadioGroup
          $rowCount={rowCount}
          aria-label="field type"
          name="type"
          value={input.value}
        >
          {availableFieldTypes.map((type: FieldType) => {
            const { icon, label, subLabel, groupLabel } =
              getFieldTypeDisplay(type);

            return (
              <React.Fragment key={type}>
                <VisuallyHidden>
                  <InputLabel htmlFor={`field-type-${type}`}>
                    {label}
                  </InputLabel>
                </VisuallyHidden>
                <TypeRadio
                  id={`field-type-${type}`}
                  inputProps={radioInputPropsWithTestId(
                    `${AI_AGENTS_PREFIX}-field-type-option-${type}`,
                  )}
                  label={
                    <>
                      <RadioLabel>
                        {(groupLabel || label).replace(subLabel ?? '', '')}
                        <RadioSubLabel>{subLabel}</RadioSubLabel>
                      </RadioLabel>

                      {icon}
                    </>
                  }
                  onChange={onChange(type)}
                  value={type}
                />
              </React.Fragment>
            );
          })}
        </RadioGroup>
        {hasError ? <FormHelperText>{meta.error}</FormHelperText> : null}
      </FormControl>
    </Main>
  );
}
